import { useSSENotification } from '@/shared/hooks/useSSENotification'
import { type NotificationItem, NotificationService } from '@/shared/services/NotificationService'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Badge, Button, Dropdown, Empty, Input, Spin, Tabs } from 'antd'
import clsx from 'clsx'
import { Bell, CheckCheck } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import './index.scss'

export const NotificationPanel: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [fetchingMore, setFetchingMore] = useState<boolean>(false)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [open, setOpen] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all')
  const [textSearch, setTextSearch] = useState('')
  const parentRef = useRef<HTMLDivElement>(null)

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await NotificationService.getNewNotification()
      const obj = res?.object !== undefined && res?.object !== null ? res.object : res?.data

      if (res?.isOk && obj !== undefined && obj !== null) {
        if (Array.isArray(obj) && obj.length > 0) {
          setUnreadCount(Number(obj[0].numberUnread ?? obj[0].numberUnseen ?? 0))
        } else if (typeof obj === 'object') {
          setUnreadCount(Number((obj as any).numberUnread ?? (obj as any).numberUnseen ?? 0))
        } else if (typeof obj === 'number') {
          setUnreadCount(obj)
        } else if (typeof obj === 'string') {
          setUnreadCount(Number(obj) || 0)
        }
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }, [])

  const fetchNotifications = useCallback(
    async (page = 1, search = textSearch, isReset = false) => {
      if (isReset) {
        setLoading(true)
        setCurrentPage(1)
      } else {
        setFetchingMore(true)
      }
      try {
        const res = await NotificationService.getListNotify({
          currentPage: page,
          pageSize: 20,
          textSearch: search,
        })
        if (res?.isOk) {
          const rawObj = res?.object !== undefined && res?.object !== null ? res.object : res?.data
          const list: NotificationItem[] = Array.isArray(rawObj)
            ? rawObj
            : Array.isArray((rawObj as any)?.data)
              ? (rawObj as any).data
              : Array.isArray((rawObj as any)?.items)
                ? (rawObj as any).items
                : []

          if (isReset || page === 1) {
            setNotifications(list)
          } else {
            setNotifications((prev) => (Array.isArray(prev) ? [...prev, ...list] : list))
          }

          setCurrentPage(page)
          setHasMore(list.length >= 20)

          // Fallback: Nếu count đang là 0 nhưng trong list có item chưa đọc
          const unreadItems = list.filter((n) => !n?.isRead).length
          setUnreadCount((prev) => (prev === 0 && unreadItems > 0 ? unreadItems : prev))
        } else {
          if (isReset || page === 1) setNotifications([])
          setHasMore(false)
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error)
        if (isReset || page === 1) setNotifications([])
      } finally {
        setLoading(false)
        setFetchingMore(false)
      }
    },
    [textSearch]
  )

  useEffect(() => {
    fetchUnreadCount()
    fetchNotifications(1, textSearch, true)
    // biome-ignore lint/correctness/useExhaustiveDependencies: chỉ chạy lần đầu
  }, [fetchUnreadCount])

  useSSENotification({
    onNotificationReceived: (newNotify) => {
      setNotifications((prev) => (Array.isArray(prev) ? [newNotify, ...prev] : [newNotify]))
      setUnreadCount((prev) => prev + 1)
    },
    onUnreadCountUpdated: (count) => {
      setUnreadCount(count)
    },
  })

  const handleOpenChange = (flag: boolean) => {
    setOpen(flag)
    if (flag) {
      if (unreadCount > 0) {
        NotificationService.markAsSeen('').then((res) => {
          if (res?.isOk) {
            fetchNotifications(1, textSearch, true)
          }
        })
      } else {
        fetchNotifications(1, textSearch, true)
      }
    }
  }

  const handleMarkAsRead = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    try {
      const res = await NotificationService.markAsRead(id)
      if (res?.isOk) {
        setNotifications((prev) =>
          (Array.isArray(prev) ? prev : []).map((n) =>
            n.notifyId === id ? { ...n, isRead: true } : n
          )
        )
        fetchUnreadCount()
      }
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }

  const handleMarkAllAsRead = async () => {
    setLoading(true)
    try {
      const res = await NotificationService.markAsRead('')
      if (res?.isOk) {
        setNotifications((prev) =>
          (Array.isArray(prev) ? prev : []).map((n) => ({ ...n, isRead: true }))
        )
        setUnreadCount(0)
        fetchUnreadCount()
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAll = async () => {
    try {
      const res = await NotificationService.deleteNotification('')
      if (res?.isOk) {
        setNotifications([])
        setUnreadCount(0)
        fetchUnreadCount()
      }
    } catch (error) {
      console.error('Failed to delete all notifications:', error)
    }
  }

  const handleItemClick = (item: NotificationItem) => {
    if (!item.isRead) {
      handleMarkAsRead(item.notifyId)
    }
  }

  const safeNotifications = Array.isArray(notifications) ? notifications : []

  const filteredNotifications = safeNotifications.filter((n) => {
    if (activeTab === 'unread') return !n?.isRead
    return true
  })

  const rowVirtualizer = useVirtualizer({
    count: hasMore ? filteredNotifications.length + 1 : filteredNotifications.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 85,
    overscan: 5,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()

  useEffect(() => {
    if (!open) return // Không fetch thêm nếu Dropdown đang đóng (tránh lỗi element height = 0)
    if (virtualItems.length === 0) return

    // Nếu element bị ẩn (height = 0), TanStack Virtual sẽ tính sai chiều cao và render tất cả
    if (parentRef.current && parentRef.current.clientHeight === 0) return

    const lastVirtualItem = virtualItems[virtualItems.length - 1]

    if (
      lastVirtualItem &&
      lastVirtualItem.index >= filteredNotifications.length - 1 &&
      hasMore &&
      !loading &&
      !fetchingMore
    ) {
      fetchNotifications(currentPage + 1, textSearch)
    }
  }, [
    open,
    virtualItems,
    filteredNotifications.length,
    hasMore,
    loading,
    fetchingMore,
    currentPage,
    textSearch,
    fetchNotifications,
  ])

  const dropdownContent = (
    <div className="notification-panel">
      <Spin spinning={loading && currentPage === 1}>
        {/* Header */}
        <div className="notification-panel__header">
          <div className="notification-panel__title">Thông báo</div>
          <div className="notification-panel__mark-read" onClick={handleMarkAllAsRead}>
            Đánh dấu đã đọc <CheckCheck size={18} />
          </div>
        </div>

        {/* Search */}
        <div className="notification-panel__search">
          <Input.Search
            placeholder="Tìm kiếm"
            onSearch={(value) => {
              setTextSearch(value)
              fetchNotifications(1, value, true)
            }}
            enterButton
          />
        </div>

        {/* Tab filters */}
        <div className="notification-panel__tabs-wrapper">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as 'all' | 'unread')}
            items={[
              {
                key: 'all',
                label: `Tất cả (${notifications.length})`,
              },
              {
                key: 'unread',
                label: `Chưa đọc (${notifications.filter((n) => !n.isRead).length})`,
              },
            ]}
          />
        </div>

        {/* Notification List */}
        <div className="notification-panel__list-container">
          {filteredNotifications.length === 0 ? (
            <div className="notification-panel__empty">
              <Empty
                description={
                  activeTab === 'unread' ? 'Không có thông báo chưa đọc' : 'Chưa có thông báo nào!'
                }
              />
            </div>
          ) : (
            <div ref={parentRef} className="notification-panel__list">
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const isLoaderRow = virtualRow.index > filteredNotifications.length - 1

                  if (isLoaderRow) {
                    return (
                      <div
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualRow.start}px)`,
                          textAlign: 'center',
                          padding: '12px 0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {fetchingMore ? (
                          <Spin size="small" />
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Cuộn để tải thêm</span>
                        )}
                      </div>
                    )
                  }

                  const item = filteredNotifications[virtualRow.index]
                  return (
                    <div
                      key={virtualRow.key}
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      className={clsx('notification-panel__item', {
                        'notification-panel__item--unread': !item.isRead,
                      })}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                      onClick={() => handleItemClick(item)}
                    >
                      <div className="notification-panel__item-content">
                        <div className="notification-panel__item-text">
                          <span className="notification-panel__item-title">{item.title}</span>
                          <br />
                        </div>
                        <div className="notification-panel__item-time">
                          {item.timeAgo || item.createDate}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="notification-panel__footer" onClick={handleDeleteAll}>
            <span>Xoá tất cả</span>
          </div>
        )}
      </Spin>
    </div>
  )

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="default" offset={[-5, 5]}>
        <Button type="text" shape="circle" icon={<Bell size={20} />} />
      </Badge>
    </Dropdown>
  )
}
